/* eslint-disable camelcase */
import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/router";
import dynamic from "next/dynamic";
import React, { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import { Button, Modal, Spinner } from "@aqar-app/react-ui";
import {
  buildFileSrc,
  CloudfrontImageLoader,
  financialFormat,
} from "@/lib/helpers";
import SEO from "@/components/seo";
import config from "@/common/config";
import { Icon } from "@/components/icon";
import { usePath } from "@/contexts/path/path";
import { FINANCING_TEXTS } from "@/graphql/queries/info";
import ListingImages from "@/components/Listing/ListingImages";
import ListingVideo from "@/components/Listing/ListingScreen/listingVideo";
import {
  GET_REAL_ESTATE_PROJECT,
  SHOW_INTEREST,
} from "@/graphql/queries/realestateProjects";
import { useAuthState } from "@/contexts/auth/auth.provider";
import { ErrorModal } from "@/components/error/ssr";
import { SuccessAlert } from "@/components/login-alert";
import { ShowPhoneButton, WhatsAppButton } from "./ProjectView";
import { getIamModalProps } from "@/components/iam/isBlocked";
import useIam, { DesiredServicesMap } from "@/hooks/useIam";
import styles from "./project.module.scss";
import { ProjectGeneralControls } from "./projectGeneralControl";
import {
  GET_LISTING,
  GET_LISTING_EXTENDED_DETAILS,
} from "@/graphql/queries/listing";
import RedBreadcrumb from "@/components/RedBreadCrumb/RedBreadCrumb";
import ExternalLink from "@/components/external-link";
import dayjs from "dayjs";
import {
  trackRegisterInterest,
  trackContactUserProject,
  trackGetProjectLocation,
} from "@/lib/tracking/trackRegisterInterest";
import {
  trackViewItem,
  unitToEECItemObject,
} from "@/lib/tracking/trackEECEvents";
import FavButton from "@/components/Listing/ListingScreen/ListingControls/favButton";
import ShareButton from "@/components/Listing/ListingScreen/ListingControls/shareButton";
import sharedStyles from "@/components/Listing/ListingScreen/.module.scss";
import { StickyContainer } from "@/components/Listing/ListingScreen/bidding-section/BiddingDetails";
import { getProjectURL } from "@/containers/project-screen/ProjectCard";
import AppLink from "@/components/new-resolver/app.ink";
import ReportButton from "@/components/Listing/ListingScreen/ListingControls/reportButton";
import { ShowMore } from "@/components/Listing/showMore";
import { NewSpecsComponent } from "@/components/Listing/table/newSpecsComponent";
import FacilitiesSection from "@/containers/project-screen/FacilitiesSection";
import {
  drawCircle,
  drawMarkerForSimpleListing,
} from "@/components/Listing/ListingScreen/DrawMarkerForSimpleListing";
import { TabsSpecs } from "@/components/Listing/table/tabsSpecs";
import ProjectUnits from "@/containers/project-screen/ProjectUnits";
import { GET_USER_REVIEWS } from "@/graphql/queries/user";
import DownPaymentRequestModal from "@/components/projectUnit/DownPaymentRequestModal";
import { trackSendPaymentRequestLink } from "@/lib/tracking/trackExclusiveUnitBookingEvents";
import { getMaintainedParam } from "@/lib/apolloClient";
import useRequestProjectLocation from "@/hooks/useRequestProjectLocation";

const UiModal = dynamic(() => import("@/components/modal/Modal"), {
  ssr: false,
});

const LeafletMap = dynamic(() =>
  import("../../components/Listing/ListingScreen/LeafletMap").then(
    (m) => m.LeafletMap,
  ),
);

export function BookUnitButton({ project, unit, iamRequired }) {
  const { previousPath } = usePath();
  const token = useAuthState("token");
  const router = useRouter();
  const [modalProps, setModalProps] = useState({ visible: false });
  const { iamStatus, isChecked } = useIam(true);

  const [BookUnit, { loading }] = useMutation(SHOW_INTEREST, {
    context: { token },
    variables: {
      project_id: project.id,
    },
  });

  const handleBookUnit = () => {
    if (!token) return router.push("/login?returnTo=" + router.asPath);
    if (!iamStatus && isChecked && iamRequired)
      return setModalProps({
        ...getIamModalProps(),
        desiredService: DesiredServicesMap.register_interest,
        onCancel: () => {
          setModalProps({ open: false });
        },
      });
    BookUnit()
      .then(() => {
        trackRegisterInterest({
          id: unit?.id,
          source: previousPath || encodeURI(document.referrer),
          journey: "pdp",
        });
        Modal.open(
          <SuccessAlert
            text={
              "تم تسجيل اهتماك بنجاح، سيتواصل معكم مطور المشروع لتأكيد الحجز"
            }
          />,
        )();
      })
      .catch(ErrorModal.handle);
  };

  return (
    <>
      <UiModal {...modalProps} />
      <ExternalLink
        link={
          (!iamStatus && isChecked && iamRequired) || !token
            ? ""
            : project.external_url
        }
      >
        <Button
          className={styles.interest}
          theme="info"
          outline
          onClick={handleBookUnit}
          disabled={loading}
        >
          {loading ? <Spinner /> : null}
          <span className={styles.icon} />
          <span className={styles.text}>تسجيل إهتمام</span>
        </Button>
      </ExternalLink>
    </>
  );
}

function UnitReservationButton({ project, unit }) {
  const { previousPath } = usePath();
  const token = useAuthState("token");
  const router = useRouter();

  const handleBookUnit = () => {
    if (!token) return router.push("/login?returnTo=" + router.asPath);
    trackSendPaymentRequestLink();

    return Modal.open(<DownPaymentRequestModal unitId={unit?.id} />)();
  };

  return (
    <Button
      className={styles.interest}
      theme="info"
      outline
      onClick={handleBookUnit}
    >
      <span className={styles.text}>إرسال الرابط</span>
    </Button>
  );
}

const FixedBottomControls = ({
  unit,
  project,
  intrestedUsersCount,
  iamRequired,
  onUnitChange,
  frozenContext,
  isExclusiveBookable,
}) => {
  const { previousPath } = usePath();
  return (
    <div
      className={`${sharedStyles.fixedMobileControlls} ${styles.mobileOnly}`}
    >
      <div className={styles.mobileControlsWrapper}>
        {isExclusiveBookable ? (
          <div className={`${styles.mobileBookUnit} `}>
            <div className={styles.interestedCount}>
              <div className={styles.title}>
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 14 14"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <rect width="14" height="14" rx="4" fill="#666666" />
                  <path
                    d="M10.3148 3.6852C9.87495 3.24642 9.27902 3 8.65773 3C8.03643 3 7.4405 3.24642 7.00064 3.6852L6.77966 3.90612C6.75063 3.93513 6.72761 3.96958 6.7119 4.00749C6.69619 4.0454 6.6881 4.08603 6.6881 4.12707C6.6881 4.16811 6.69618 4.20874 6.71188 4.24666C6.72758 4.28457 6.7506 4.31902 6.77961 4.34804L7.22143 4.78984C7.28002 4.84844 7.35948 4.88135 7.44234 4.88136C7.52521 4.88137 7.60468 4.84846 7.66328 4.78988L7.88433 4.5689C8.08946 4.36381 8.36764 4.2486 8.65771 4.24862C8.94777 4.24863 9.22595 4.36386 9.43105 4.56896C9.63615 4.77407 9.75139 5.05225 9.7514 5.34231C9.75141 5.63237 9.6362 5.91056 9.43111 6.11568L8.32619 7.22029C8.1326 7.41172 7.87493 7.5246 7.60297 7.53711C7.331 7.54963 7.06405 7.46088 6.85371 7.28804L6.61392 7.08777C6.58242 7.06145 6.54605 7.04161 6.50688 7.02936C6.46771 7.01711 6.4265 7.01269 6.38563 7.01637C6.34475 7.02005 6.30499 7.03174 6.26864 7.05079C6.23228 7.06983 6.20003 7.09585 6.17373 7.12736L5.77326 7.60717C5.72016 7.6708 5.69451 7.75291 5.70195 7.83545C5.70939 7.91799 5.74931 7.9942 5.81293 8.0473L6.05272 8.24742C6.50073 8.62463 7.07435 8.81933 7.65941 8.79275C8.24448 8.76617 8.79808 8.52028 9.21005 8.104L10.315 6.99938C10.7544 6.55986 11.0013 5.96378 11.0012 5.34226C11.0012 4.72074 10.7543 4.12468 10.3148 3.6852Z"
                    fill="white"
                  />
                  <path
                    d="M6.77991 9.20897C6.72131 9.15038 6.64185 9.11746 6.55899 9.11745C6.47613 9.11744 6.39666 9.15035 6.33805 9.20893L6.117 9.42991C5.91188 9.635 5.63369 9.75021 5.34363 9.7502C5.05356 9.75018 4.77539 9.63495 4.57028 9.42985C4.36518 9.22474 4.24994 8.94656 4.24993 8.6565C4.24992 8.36644 4.36513 8.08825 4.57022 7.88313L5.67515 6.77852C5.86865 6.58695 6.12635 6.47397 6.39836 6.46146C6.67036 6.44894 6.93734 6.53778 7.14762 6.71077L7.38741 6.91104C7.41891 6.93736 7.45528 6.9572 7.49445 6.96946C7.53363 6.98171 7.57483 6.98612 7.61571 6.98244C7.65658 6.97877 7.69634 6.96707 7.7327 6.94803C7.76905 6.92898 7.8013 6.90296 7.8276 6.87145L8.22807 6.39164C8.28117 6.32801 8.30682 6.2459 8.29938 6.16336C8.29194 6.08082 8.25201 6.00461 8.18839 5.95151L7.9486 5.75139C7.5006 5.37415 6.92697 5.17944 6.3419 5.20602C5.75683 5.23259 5.20322 5.47851 4.79127 5.89481L3.68637 6.99943C3.24688 7.43894 2.99999 8.03503 3 8.65657C3.00001 9.27812 3.24693 9.8742 3.68644 10.3137C4.12595 10.7532 4.72204 11.0001 5.34358 11.0001C5.96513 11 6.56121 10.7531 7.0007 10.3136L7.22168 10.0927C7.2507 10.0637 7.27372 10.0292 7.28943 9.99132C7.30514 9.95341 7.31323 9.91278 7.31323 9.87174C7.31323 9.8307 7.30516 9.79007 7.28945 9.75215C7.27375 9.71424 7.25073 9.67979 7.22172 9.65077L6.77991 9.20897Z"
                    fill="white"
                  />
                </svg>
                <div style={{ fontWeight: 600 }}>
                  مبلغ العربون{" "}
                  <span>
                    (
                    {financialFormat(
                      project?.project_cash_down_payment,
                      " ريال",
                    )}
                    )
                  </span>
                </div>
              </div>
              <div className={styles.title}>
                أرسل رابط الدفع للعميل لحجز الوحدة
              </div>
            </div>
            <div className={styles.submitInterest}>
              <UnitReservationButton unit={{ id: unit.id }} />
            </div>
          </div>
        ) : null}
        <div
          className={`${styles.mobileControls} ${
            !isExclusiveBookable ? styles.noTop : ""
          }`}
        >
          <ProjectGeneralControls
            onUnitChange={onUnitChange}
            unit={unit}
            frozenContext={frozenContext}
            compact
            onWhatsAppClick={() => {
              trackContactUserProject({
                source: previousPath || encodeURI(document.referrer),
              });
            }}
          />
        </div>
      </div>
    </div>
  );
};

const ProjectUnitView = ({ frozenContext }) => {
  const { user } = useAuthState();
  const {
    fields,
    updateContext,
    project: projectCtx,
    unit: unitCtx,
    extendedFields,
    categoriesDictionary,
    previousPath,
  } = usePath();

  useEffect(() => {
    trackViewItem(
      unitToEECItemObject(unitCtx, undefined, categoriesDictionary),
      { value: unitCtx.price },
    );
  }, [unitCtx?.id]);

  const projectId = projectCtx?.id;
  const unitId = unitCtx?.id;
  const {
    data: {
      Info: {
        configurations: { financing_texts: financing },
      },
    } = { Info: { configurations: {} } },
  } = useQuery(FINANCING_TEXTS);

  const maintainedParam = getMaintainedParam(user?.cooperative);

  const { data: projectRes } = useQuery(GET_REAL_ESTATE_PROJECT, {
    variables: {
      project_id: parseInt(projectId, 10),
      where: {
        project_id: { eq: parseInt(projectId, 10) },
        published: { eq: 1 },
        ...maintainedParam,
      },
    },
  });
  const [unit, setUnit] = useState({});
  const { data } = useQuery(GET_LISTING, {
    skip: !unitId,
    variables: { id: parseInt(unitId, 10) },
    onCompleted: (data) => {
      if (data?.Web?.listing) {
        setUnit({
          ...data?.Web?.listing,
          create_time: dayjs(data?.Web?.listing.create_time * 1000).format(
            "DD/MM/YYYY",
          ),
        });
      }
    },
  });

  const { data: extended } = useQuery(GET_LISTING_EXTENDED_DETAILS, {
    skip: !unitId,
    variables: { id: parseInt(unitId, 10) },
  });

  const {
    data: {
      User: {
        reviews: { total },
      },
    } = { User: { reviews: {} } },
    loading: loadingReview,
  } = useQuery(GET_USER_REVIEWS, {
    skip: !unit?.user_id,
    variables: {
      user_id: unit?.user_id,
    },
  });

  const { handleRequestLocation } = useRequestProjectLocation(projectId);

  const project = projectRes?.RealEstateProjects?.getProject || {};
  const realEstateDeveloper = project?.real_estate_developer || {};
  const projectURL = getProjectURL(project);
  const projectListings =
    projectRes?.Search?.find?.projects?.[0]?.listings || [];

  const hasFinancing = unit?.title?.includes("للبيع");
  const extended_details = extended?.Listing?.getListingExtendedDetails || [];
  const userReviews = unit.user?.review;
  const hasReviews = !loadingReview && !!userReviews && total;
  const isExclusiveBookable =
    projectRes?.Search?.find?.projects?.[0]?.is_exclusive &&
    user?.permissions?.includes(
      "ACCESS_TO_INIT_REAL_ESTATE_PROJECT_PAYMENT_REQUEST",
    );

  const {
    bookable_unit_interested_users_count: intrestedUsersCount,
    bookable_unit_plans: unitPlans,
  } = unit || {};
  const {
    meta_data,
    virtual_tour: virtualTour,
    require_iam_to_book: iamRequired,
    introducing_document,
  } = project || {};

  const metaData = useMemo(() => {
    const features = meta_data?.features?.filter((m) => m.value) || [];
    const support_banks =
      meta_data?.support_banks?.filter((m) => m.value) || [];
    const facilities = meta_data?.facilities?.filter((m) => m.value) || [];
    const warranty_and_maintenance =
      meta_data?.warranty_and_maintenance?.filter((m) => m.value?.ar) || [];
    const after_sale_services =
      meta_data?.after_sale_services?.filter((m) => m.value) || [];

    return {
      features,
      support_banks,
      facilities,
      warranty_and_maintenance,
      after_sale_services,
    };
  }, [meta_data]);

  const renderPlanButtons = () => {
    return (
      <>
        <ExternalLink link={virtualTour?.length && virtualTour[0]?.link}>
          <Button outline disabled={!virtualTour?.length}>
            <Icon name="virtualTour" width={12} height={12} />
            الجولة الإفتراضية
          </Button>
        </ExternalLink>
        <ExternalLink
          link={
            introducing_document &&
            `https://cdn.aqar.fm/projects/${introducing_document}`
          }
        >
          <Button outline disabled={!introducing_document}>
            <Icon name="intreducingFile" width={12} height={12} />
            ملف المشروع
          </Button>
        </ExternalLink>
      </>
    );
  };

  const renderDeveloperInfo = (size) => {
    return (
      <div
        className={`${sharedStyles.ownerWrapper} ${
          size === "mobile" ? styles.mobileOnly : styles.desktopOnly
        }`}
        style={{ gap: "0.5rem" }}
      >
        <h4>معلومات المطور</h4>
        <div className={`${sharedStyles.card} ${styles.developerCard}`}>
          <div className={styles.top}>
            <div className={styles.developerProfile}>
              <div
                onClick={() => {
                  updateContext({
                    project: null,
                    unit: null,
                    developer: {
                      ...realEstateDeveloper,
                      url: getProjectURL({
                        real_estate_developer: realEstateDeveloper,
                      }),
                    },
                  });
                }}
                className={styles.avatar}
              >
                <Image
                  loader={CloudfrontImageLoader}
                  src={`https://cdn.aqar.fm/projects/${realEstateDeveloper?.logo_img}`}
                  fill
                  alt="logo"
                  sizes="(max-width: 600px) 40vw, 100vw"
                />
              </div>
              <div className={styles.developerInfo}>
                <AppLink
                  onClick={() => {
                    updateContext({
                      project: null,
                      unit: null,
                      developer: {
                        ...realEstateDeveloper,
                        url: getProjectURL({
                          real_estate_developer: realEstateDeveloper,
                        }),
                      },
                    });
                  }}
                  path={getProjectURL({
                    real_estate_developer: realEstateDeveloper,
                  })}
                  className={styles.name}
                >
                  {realEstateDeveloper?.name}
                </AppLink>
                <div className={styles.about}>
                  {hasReviews ? (
                    <>
                      <Icon width={15} height={15} name="v1/star" />
                      <p>
                        <span>
                          {Intl.NumberFormat("en", {
                            maximumFractionDigits: 1,
                          }).format(userReviews)}
                        </span>
                        <span> التقييمات ({total})</span>
                      </p>
                    </>
                  ) : (
                    <div>لا يوجد تقييم بعد</div>
                  )}
                </div>
              </div>
            </div>
            <div className={styles.contactBtns}>
              <WhatsAppButton
                project={project}
                btnStyle={{ borderColor: "#E1E6EB", flex: 1 }}
                compact
                unitId={unitId}
                onClick={() => {
                  trackContactUserProject({
                    source: previousPath || encodeURI(document.referrer),
                  });
                }}
              />
            </div>
          </div>
          {isExclusiveBookable ? (
            <div className={styles.bottom}>
              <div className={styles.interestedCount}>
                <div className={styles.title} style={{ alignItems: "center" }}>
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 14 14"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <rect width="14" height="14" rx="4" fill="#666666" />
                    <path
                      d="M10.3148 3.6852C9.87495 3.24642 9.27902 3 8.65773 3C8.03643 3 7.4405 3.24642 7.00064 3.6852L6.77966 3.90612C6.75063 3.93513 6.72761 3.96958 6.7119 4.00749C6.69619 4.0454 6.6881 4.08603 6.6881 4.12707C6.6881 4.16811 6.69618 4.20874 6.71188 4.24666C6.72758 4.28457 6.7506 4.31902 6.77961 4.34804L7.22143 4.78984C7.28002 4.84844 7.35948 4.88135 7.44234 4.88136C7.52521 4.88137 7.60468 4.84846 7.66328 4.78988L7.88433 4.5689C8.08946 4.36381 8.36764 4.2486 8.65771 4.24862C8.94777 4.24863 9.22595 4.36386 9.43105 4.56896C9.63615 4.77407 9.75139 5.05225 9.7514 5.34231C9.75141 5.63237 9.6362 5.91056 9.43111 6.11568L8.32619 7.22029C8.1326 7.41172 7.87493 7.5246 7.60297 7.53711C7.331 7.54963 7.06405 7.46088 6.85371 7.28804L6.61392 7.08777C6.58242 7.06145 6.54605 7.04161 6.50688 7.02936C6.46771 7.01711 6.4265 7.01269 6.38563 7.01637C6.34475 7.02005 6.30499 7.03174 6.26864 7.05079C6.23228 7.06983 6.20003 7.09585 6.17373 7.12736L5.77326 7.60717C5.72016 7.6708 5.69451 7.75291 5.70195 7.83545C5.70939 7.91799 5.74931 7.9942 5.81293 8.0473L6.05272 8.24742C6.50073 8.62463 7.07435 8.81933 7.65941 8.79275C8.24448 8.76617 8.79808 8.52028 9.21005 8.104L10.315 6.99938C10.7544 6.55986 11.0013 5.96378 11.0012 5.34226C11.0012 4.72074 10.7543 4.12468 10.3148 3.6852Z"
                      fill="white"
                    />
                    <path
                      d="M6.77991 9.20897C6.72131 9.15038 6.64185 9.11746 6.55899 9.11745C6.47613 9.11744 6.39666 9.15035 6.33805 9.20893L6.117 9.42991C5.91188 9.635 5.63369 9.75021 5.34363 9.7502C5.05356 9.75018 4.77539 9.63495 4.57028 9.42985C4.36518 9.22474 4.24994 8.94656 4.24993 8.6565C4.24992 8.36644 4.36513 8.08825 4.57022 7.88313L5.67515 6.77852C5.86865 6.58695 6.12635 6.47397 6.39836 6.46146C6.67036 6.44894 6.93734 6.53778 7.14762 6.71077L7.38741 6.91104C7.41891 6.93736 7.45528 6.9572 7.49445 6.96946C7.53363 6.98171 7.57483 6.98612 7.61571 6.98244C7.65658 6.97877 7.69634 6.96707 7.7327 6.94803C7.76905 6.92898 7.8013 6.90296 7.8276 6.87145L8.22807 6.39164C8.28117 6.32801 8.30682 6.2459 8.29938 6.16336C8.29194 6.08082 8.25201 6.00461 8.18839 5.95151L7.9486 5.75139C7.5006 5.37415 6.92697 5.17944 6.3419 5.20602C5.75683 5.23259 5.20322 5.47851 4.79127 5.89481L3.68637 6.99943C3.24688 7.43894 2.99999 8.03503 3 8.65657C3.00001 9.27812 3.24693 9.8742 3.68644 10.3137C4.12595 10.7532 4.72204 11.0001 5.34358 11.0001C5.96513 11 6.56121 10.7531 7.0007 10.3136L7.22168 10.0927C7.2507 10.0637 7.27372 10.0292 7.28943 9.99132C7.30514 9.95341 7.31323 9.91278 7.31323 9.87174C7.31323 9.8307 7.30516 9.79007 7.28945 9.75215C7.27375 9.71424 7.25073 9.67979 7.22172 9.65077L6.77991 9.20897Z"
                      fill="white"
                    />
                  </svg>
                  <div style={{ fontWeight: 600 }}>
                    مبلغ العربون{" "}
                    <span>
                      (
                      {financialFormat(
                        project?.project_cash_down_payment,
                        " ريال",
                      )}
                      )
                    </span>
                  </div>
                </div>
                <div className={styles.title} style={{ marginTop: "0.5rem" }}>
                  أرسل رابط الدفع للعميل لحجز الوحدة
                </div>
              </div>
              <div className={styles.submitInterest}>
                <UnitReservationButton unit={{ id: unit.id }} />
              </div>
            </div>
          ) : null}
        </div>
        <div className={`${sharedStyles.card} ${styles.projectCard}`}>
          <div className={styles.projectDetails}>
            <Icon name="Villa" width={40} height={40} />
            <div className={styles.projectInfo}>
              <div className={styles.disclaimer}>هذه الوحدة تابعة لمشروع</div>
              <div className={styles.title}>{project?.title}</div>
            </div>
          </div>
          <div className={styles.projectLink}>
            <AppLink
              onClick={() =>
                updateContext({
                  developer: realEstateDeveloper,
                  project: {
                    ...project,
                    url: projectURL,
                  },
                  unit: null,
                })
              }
              path={projectURL}
              scroll
            >
              تصفح المشروع
            </AppLink>
            <Icon name="leftArrow-searches" width={12} height={12} />
          </div>
        </div>
        {unit.user_id !== user?.user_id ? (
          <div className={styles.report}>
            <ReportButton listingID={unit?.id} />
          </div>
        ) : null}
      </div>
    );
  };

  return (
    <div className={styles.projectScreen}>
      <SEO
        title={`المشاريع العقارية | ${project?.real_estate_developer?.name}| ${project?.title} |تطبيق عقار`}
        description={project?.description}
        image={
          unit?.imgs?.[0]
            ? `https://images.aqar.fm/webp/350x0/props/${unit?.imgs?.[0]}`
            : null
        }
      />
      <div className={styles.projectContainer}>
        <RedBreadcrumb />
        <h1 className={styles.title}>{unit?.title}</h1>
        <div className={styles.galleryWrapper}>
          <div className={styles.controls}>
            <FavButton listing={unit} />
            <ShareButton type="project_unit" listing={unit} />
          </div>
          {unit?.imgs?.length ? <ListingImages listing={unit} /> : null}
        </div>
        <div className={sharedStyles.towColumns}>
          <div>
            <div className={styles.financing}>
              <p className={styles.price}>
                {financialFormat(unit?.price, " ريال")}
              </p>
              {hasFinancing ? (
                <div style={{ width: "100%" }}>
                  <p>{financing?.promotional_text?.ar}</p>
                  <Link href={`/financing/application?listingId=${unit.id}`}>
                    <button type="button">{financing?.button_text?.ar}</button>
                  </Link>
                </div>
              ) : null}
            </div>

            {!!unit.content && (
              <div
                className={`${sharedStyles.card} ${sharedStyles.bottomBorderOnly} ${sharedStyles.flex}`}
              >
                <ShowMore lines={2}>{unit.content}</ShowMore>
              </div>
            )}

            <div className={styles.unitSpec}>
              <NewSpecsComponent
                fields={fields}
                extendedFields={extendedFields}
                listing={unit}
              />
            </div>

            <FacilitiesSection facilities={metaData?.facilities} />

            <div
              className={`${styles.section} ${styles.noBorderBottomOnMobile}`}
              style={{ gap: ".5rem" }}
            >
              <div className={styles.plans}>
                <h4>تعرف أكثر</h4>
                <div className={`${styles.plansBtns} ${styles.onlyDesktop}`}>
                  {renderPlanButtons()}
                </div>
              </div>
              {!!unit?.has_video && (
                <ListingVideo videoItem={(unit?.videos || [])[0]} />
              )}
              <div className={styles.plans}>
                <div className={`${styles.plansBtns} ${styles.onlyMobile}`}>
                  {renderPlanButtons()}
                </div>
              </div>
            </div>

            {!!metaData?.warranty_and_maintenance?.length && (
              <div
                className={`${styles.facilitiesSection} ${sharedStyles.card} ${sharedStyles.bottomBorderOnly} ${sharedStyles.flex}`}
              >
                <div className={styles.title}>
                  <h4>الضمان والصيانة</h4>
                </div>
                <div className={styles.facilitiesList}>
                  {metaData?.warranty_and_maintenance.map((service, index) => (
                    <div key={index} className={styles.facilityItem}>
                      <Image
                        src={service.icon_url_svg}
                        width={36}
                        height={36}
                        alt={service.meta_key_name?.ar}
                      />
                      <span className={styles.label}>
                        {service.meta_key_name?.ar}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {!!unitPlans?.length && (
              <div
                className={`${styles.facilitiesSection} ${sharedStyles.card} ${sharedStyles.bottomBorderOnly} ${sharedStyles.flex} ${styles.noBorderBottomOnMobile}`}
              >
                <div className={styles.title}>
                  <h4>المخطط</h4>
                </div>
                <div className={styles.planWrapper}>
                  <Image
                    src={buildFileSrc(
                      `unites-plans/${unitPlans[0]}`,
                      config.IMAGES_CDN_BASE_URL,
                    )}
                    fill
                    alt="plan"
                  />
                </div>
              </div>
            )}

            {!!metaData?.after_sale_services?.length && (
              <div
                className={`${styles.facilitiesSection} ${sharedStyles.card} ${sharedStyles.bottomBorderOnly} ${sharedStyles.flex}`}
              >
                <div className={styles.title}>
                  <h4>خدمات ما بعد البيع</h4>
                </div>
                <div className={styles.facilitiesList}>
                  {metaData?.after_sale_services.map((service, index) => (
                    <div key={index} className={styles.facilityItem}>
                      <Image
                        src={service.icon_url_svg}
                        width={36}
                        height={36}
                        alt={service.meta_key_name?.ar}
                      />
                      <span className={styles.label}>
                        {service.meta_key_name?.ar}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {unit?.location && (
              <div className={styles.facilitiesSection}>
                <div style={{ marginBottom: "1rem" }}>
                  <h4>الموقع</h4>
                </div>
                <LeafletMap
                  onMapReady={drawCircle(
                    { location: unit.location },
                    () => {
                      trackGetProjectLocation({
                        source: previousPath || encodeURI(document.referrer),
                        listingType: categoriesDictionary[unitCtx.category]?.ga_listing_type,
                        propertyCategory: categoriesDictionary[unitCtx.category]?.ga_property_category,
                        page: "unit_page",
                      })
                      return handleRequestLocation();
                    },
                  )}
                  className={sharedStyles.mapWrapper}
                />
              </div>
            )}

            {renderDeveloperInfo("mobile")}

            <TabsSpecs listing={unit} />

            <ProjectGeneralControls
              onUnitChange={(ev) => {
                updateContext(ev);
              }}
              unit={unit}
              frozenContext={frozenContext}
            />
          </div>

          <StickyContainer className={sharedStyles.sticky}>
            {renderDeveloperInfo()}
          </StickyContainer>
        </div>

        {!!projectListings?.length && (
          <div className={styles.projectUnitsSection}>
            <ProjectUnits
              listings={projectRes?.Search?.find?.projects?.listings || []}
              project={project}
              title={`وحدات المشروع (${projectListings.length})`}
              hideFilters
            />
          </div>
        )}

        <FixedBottomControls
          unit={unit}
          project={project}
          intrestedUsersCount={intrestedUsersCount}
          iamRequired={iamRequired}
          onUnitChange={(ev) => {
            updateContext(ev);
          }}
          frozenContext={frozenContext}
          isExclusiveBookable={isExclusiveBookable}
        />
      </div>
    </div>
  );
};

export default ProjectUnitView;
